import axiosInstance from './axiosInstance'

const PARROQUIA_BASE = '/parish'

let parroquiasCache = null
let cacheTimestamp = null
const CACHE_TTL = 5 * 60 * 1000 // 5 minutos

/**
 * Invalida el cache de parroquias
 */
export const invalidateParroquiasCache = () => {
  parroquiasCache = null
  cacheTimestamp = null
}

/**
 * Detecta la parroquia basándose en coordenadas (lat, lng)
 * @param {number} lat - Latitud
 * @param {number} lng - Longitud
 * @returns {Promise<Object>} { detected: boolean, parroquia?: Object, canton?: Object }
 */
export const detectParroquiaByCoordinates = async (lat, lng) => {
  try {
    const response = await axiosInstance.post(`${PARROQUIA_BASE}/detect`, { lat, lng })
    return response.data
  } catch (error) {
    console.error('Error detecting parroquia:', error)
    return {
      detected: false,
      message: error.response?.data?.message || 'Error al detectar parroquia',
    }
  }
}

/**
 * Obtiene todas las parroquias habilitadas
 * @returns {Promise<Array>} Lista de parroquias
 */
export const fetchEnabledParroquias = async () => {
  const now = Date.now()

  if (parroquiasCache && cacheTimestamp && now - cacheTimestamp < CACHE_TTL) {
    return parroquiasCache
  }

  try {
    const response = await axiosInstance.get(`${PARROQUIA_BASE}/enabled`)
    parroquiasCache = response.data.parroquias || []
    cacheTimestamp = now
    return parroquiasCache
  } catch (error) {
    console.error('Error fetching parroquias:', error)
    return parroquiasCache || []
  }
}

/**
 * Obtiene una parroquia por slug
 * @param {string} slug - Slug de la parroquia
 * @returns {Promise<Object|null>} Parroquia o null
 */
export const fetchParroquiaBySlug = async slug => {
  if (parroquiasCache) {
    const cached = parroquiasCache.find(p => p.slug === slug)
    if (cached) return cached
  }

  try {
    const response = await axiosInstance.get(`${PARROQUIA_BASE}/by-slug/${slug}`)
    return response.data.parroquia || null
  } catch (error) {
    console.error(`Error fetching parroquia ${slug}:`, error)
    return null
  }
}

/**
 * Obtiene el boundary GeoJSON de una parroquia
 * @param {string} slug - Slug de la parroquia
 * @returns {Promise<Object|null>} GeoJSON o null
 */
export const fetchParroquiaBoundary = async slug => {
  try {
    const response = await axiosInstance.get(`${PARROQUIA_BASE}/by-slug/${slug}/boundary`)
    return response.data
  } catch (error) {
    console.error(`Error fetching boundary for parroquia ${slug}:`, error)
    return null
  }
}

/**
 * Obtiene todas las parroquias (para administración)
 * @returns {Promise<Array>} Lista de parroquias
 */
export const fetchAllParroquias = async () => {
  try {
    const response = await axiosInstance.get(PARROQUIA_BASE)
    const rawData = response.data.parroquias || response.data.res || []
    return rawData.map(p => ({
      id: p.id || p.id_pq,
      nombre: p.nombre,
      slug: p.slug,
      idCanton: p.idCanton || p.id_canton,
      cantonNombre: p.canton?.nombre || p.cantonNombre || '',
      tipo: p.tipo || 'urbana',
      habilitado: Boolean(p.habilitado),
      hasGeojson: Boolean(p.hasGeojson),
      center_lng: p.center_lng,
      center_lat: p.center_lat,
    }))
  } catch (error) {
    console.error('Error fetching all parroquias:', error)
    return []
  }
}

/**
 * Obtiene parroquias por cantón
 * @param {number} cantonId - ID del cantón
 * @returns {Promise<Array>} Lista de parroquias del cantón
 */
export const fetchParroquiasByCanton = async cantonId => {
  try {
    const response = await axiosInstance.get(`${PARROQUIA_BASE}/by-canton/${cantonId}`)
    return response.data.parroquias || []
  } catch (error) {
    console.error(`Error fetching parroquias for canton ${cantonId}:`, error)
    return []
  }
}

/**
 * Crea una nueva parroquia
 * @param {Object} data - Datos de la parroquia
 */
export const createParroquia = async data => {
  try {
    const response = await axiosInstance.post(PARROQUIA_BASE, data)
    invalidateParroquiasCache()
    return { success: true, parroquia: response.data.parroquia }
  } catch (error) {
    console.error('Error creating parroquia:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al crear la parroquia',
    }
  }
}

/**
 * Actualiza una parroquia existente
 * @param {number} id - ID de la parroquia
 * @param {Object} data - Datos a actualizar
 */
export const updateParroquia = async (id, data) => {
  try {
    const response = await axiosInstance.put(`${PARROQUIA_BASE}/${id}`, data)
    invalidateParroquiasCache()
    return { success: true, parroquia: response.data.parroquia }
  } catch (error) {
    console.error('Error updating parroquia:', error)
    console.error('Response data:', error.response?.data)
    const errorMessage = error.response?.data?.message
      || error.response?.data?.error
      || error.response?.data?.errors?.join(', ')
      || 'Error al actualizar la parroquia'
    return {
      success: false,
      message: errorMessage,
    }
  }
}

/**
 * Elimina una parroquia
 * @param {number} id - ID de la parroquia
 */
export const deleteParroquia = async id => {
  try {
    await axiosInstance.delete(`${PARROQUIA_BASE}/${id}`)
    invalidateParroquiasCache()
    return { success: true }
  } catch (error) {
    console.error('Error deleting parroquia:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al eliminar la parroquia',
    }
  }
}

/**
 * Habilita una parroquia
 * @param {number} id - ID de la parroquia
 */
export const enableParroquia = async id => {
  try {
    const response = await axiosInstance.patch(`${PARROQUIA_BASE}/${id}/enable`)
    invalidateParroquiasCache()
    return { success: true, parroquia: response.data.parroquia }
  } catch (error) {
    console.error('Error enabling parroquia:', error)
    return { success: false, message: 'Error al habilitar la parroquia' }
  }
}

/**
 * Deshabilita una parroquia
 * @param {number} id - ID de la parroquia
 */
export const disableParroquia = async id => {
  try {
    const response = await axiosInstance.patch(`${PARROQUIA_BASE}/${id}/disable`)
    invalidateParroquiasCache()
    return { success: true, parroquia: response.data.parroquia }
  } catch (error) {
    console.error('Error disabling parroquia:', error)
    return { success: false, message: 'Error al deshabilitar la parroquia' }
  }
}
